/**
 * Import Swiper and its css.
 */
import Swiper from "swiper";
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import "swiper/css/bundle";

/**
 * SliderHandler
 *
 * This class handles the 4 different types of sliders on the website.
 * link_objs, images, textwithicons, pure_images
 *
 * @author Christian Werner
 */
export default class SliderHandler {
	/**
	 * Constructor
	 *
	 * @author Christian Werner
	 * @since 1.0.0
	 */
	constructor() {
		this.getDependencies();
		if (!this.dependenciesAvailable()) {
			return;
		}

		this.initSwiper();
	}

	/**
	 * Checks if all dependencies (HTMLElements) exist
	 *
	 * @returns {boolean} true if all elements available (not null or undefined), otherwise false
	 */
	dependenciesAvailable = () => {
		if (this.sliderWrappers == null || this.sliderWrappers.length <= 0) return false;
		return true;
	};

	/**
	 * Retrieves the needed dependencies (HTMLElements)
	 *
	 * @returns {void}
	 */
	getDependencies = () => {
		this.sliderWrappers = document.querySelectorAll(".js__swiper-container");
	};

	/**
	 * Initialise the swiper.
	 *
	 * @author Christian Werner
	 * @since 1.0.0
	 *
	 * @return void
	 */
	initSwiper() {
		this.sliderWrappers.forEach((sliderWrapper, key, parent) => {
			const isAutoplay = sliderWrapper.dataset.sliderAutoplay === '1' ? true : false;
			const desktopSpeedInMs = sliderWrapper.dataset.sliderMsDesktop;
			const mobileSpeedInMs = sliderWrapper.dataset.sliderMsMobile;
			const sliderType = sliderWrapper.dataset.sliderType;
			let hasNavigation = false;
			let hasPagination = true;

			let paginationOptions = {
				el: '.swiper-pagination-custom-bar',
				bulletClass: 'swiper-pagination-bullet-as-rect',
				bulletActiveClass: 'swiper-pagination-bullet-as-rect-active',
			}

			let slidesPerView = {
				mobile: 1,
				tablet: 2,
				desktop: 4,
			}

			let centeredSlidesMobile = false;

			let isLoop = {
				mobile: false,
				tablet: false,
				desktop: false,
			}

			if (sliderType === 'images') {
				// former: slider-logo

				/**
				 * Altering the variables for the pure_images slidesPerView
				 */
				slidesPerView.mobile = "auto";
				slidesPerView.tablet = 3;
				slidesPerView.desktop = 4;

				/**
				 * Activating centeredSlides for the logo slider
				 */
				centeredSlidesMobile = true;
				isLoop.mobile = true;

			} else if (sliderType === 'pure_images') {
				// former: slider-project

				/**
				 * Altering the variables for the pure_images slidesPerView
				 */
				slidesPerView.mobile = 1;
				slidesPerView.tablet = 1;
				slidesPerView.desktop = 1;

				/**
				 * Altering the variables for the pure_images pagination
				 */
				paginationOptions.el = '.swiper-pagination-custom-bar-for-images';

				/**
				 * Activating the navigation for the pure_images slider
				 */
				hasNavigation = true;

			} else if (sliderType === 'textwithicons') {
				/**
				 * Altering the variables for the pure_images slidesPerView
				 */
				slidesPerView.mobile = "auto";
				slidesPerView.desktop = 3;

				/**
				 * Altering the variables for the pure_images pagination
				 */
				paginationOptions.el = '.swiper-pagination-custom-bar-extended';

			} else if (sliderType === 'blog') {
				/**
				 * Altering the variables for the pure_images slidesPerView
				 */
				slidesPerView.mobile = "auto";
				slidesPerView.tablet = "auto";
				slidesPerView.desktop = "auto";

				/**
				 * Altering the variables for the pagination
				 */
				hasPagination = false;

				/** 
				 * Setting the loop to true, so the slider can be infinite
				 * This is important for the blog slider, because of its layout/design
				 */
				isLoop.mobile = true;
				isLoop.tablet = true;
				isLoop.desktop = true;

				/**
				 * Activating centeredSlides for the blog slider because it looks better
				 */
				centeredSlidesMobile = true;
			}

			const swiper = new Swiper(sliderWrapper, {
				spaceBetween: 16,
				slidesPerView: slidesPerView.mobile,
				modules: [Navigation, Pagination, Autoplay],
				centeredSlides: centeredSlidesMobile,
				loop: isLoop.mobile,
				autoplay: isAutoplay,
				speed: parseInt(mobileSpeedInMs),

				navigation: {
					enabled: hasNavigation,
					nextEl: '.swiper-navigation-next',
					prevEl: '.swiper-navigation-prev',
				},

				pagination: {
					...paginationOptions,
					enabled: hasPagination,
					clickable: true,
					type: 'bullets',
					renderBullet: function (index, className) {
						return `<span class="${className}"></span>`;
					}
				},
				breakpoints: {
					770: {
						spaceBetween: 24,
						slidesPerView: slidesPerView.tablet,
						speed: parseInt(desktopSpeedInMs),
						centeredSlides: false,
						loop: isLoop.tablet,
					},
					1048: {
						spaceBetween: 32,
						slidesPerView: slidesPerView.desktop,
						centeredSlides: false,
						speed: parseInt(desktopSpeedInMs),
						loop: isLoop.desktop,
					},
				}
			});
		});
	}
}